import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { as2CertificatesApi } from '../api/as2Certificates';
import { listAs2DlqTasks } from '../api/as2Dlq';
import { listAs2InboundMessages } from '../api/as2InboundMessages';
import { listAs2OutboundMessages } from '../api/as2OutboundMessages';
import { PageHeader } from '../components/FeatureStatus';
import { PollingIndicator } from '../components/PollingIndicator';
import { useAsyncData } from '../hooks/useAsyncData';
import type { As2CertificateHealthStatus, As2MessageStatus } from '../types/api';

// Landing page for the AS2 Gateway nav group. Every tile below is backed by a real endpoint
// call (certificate health, DLQ list-with-size:1, message list-with-size:1 read via `.total`)
// — there is no dedicated stats/aggregate endpoint on the gateway, so this deliberately avoids
// fabricated metrics or time-series charts. Certificate health and DLQ tiles link back to the
// existing Certificates/DLQ pages without a pre-applied filter (those pages don't read filter
// state from the URL and are out of scope to modify here); outbound/inbound message tiles link
// with a `status` filter that the new As2Outbound/InboundMessagesPage components do read on load.

const HEALTH_STATUSES: As2CertificateHealthStatus[] = ['VALID', 'EXPIRING_SOON', 'CRITICAL', 'EXPIRED'];

// Deliberately longer than the 15-30s default other pages use: this dashboard fans out to 11
// separate size:1 list calls (each cheap individually, but 11x per poll tick adds up), so a
// shorter interval would multiply an already call-count-heavy page.
const AS2_DASHBOARD_POLL_INTERVAL_MS = 45_000;

export function As2DashboardPage() {
  const { search } = useLocation();
  const [pollingPaused, setPollingPaused] = useState(false);
  const pollIntervalMs = pollingPaused ? undefined : AS2_DASHBOARD_POLL_INTERVAL_MS;

  const { data: health, isLoading: healthLoading } = useAsyncData(() => as2CertificatesApi.health(), [], { pollIntervalMs });
  const { data: dlqTotal, isLoading: dlqTotalLoading } = useAsyncData(() => listAs2DlqTasks({ size: 1 }), [], { pollIntervalMs });
  const { data: dlqDeadLettered, isLoading: dlqDeadLetteredLoading } = useAsyncData(
    () => listAs2DlqTasks({ size: 1, status: 'DEAD_LETTERED' }),
    [],
    { pollIntervalMs },
  );

  const { data: outboundTotal, isLoading: outboundTotalLoading } = useAsyncData(() => listAs2OutboundMessages({ size: 1 }), [], { pollIntervalMs });
  const { data: outboundFailed, isLoading: outboundFailedLoading } = useAsyncData(
    () => listAs2OutboundMessages({ size: 1, status: 'FAILED' }),
    [],
    { pollIntervalMs },
  );
  const { data: outboundDeadLettered, isLoading: outboundDeadLetteredLoading } = useAsyncData(
    () => listAs2OutboundMessages({ size: 1, status: 'DEAD_LETTERED' }),
    [],
    { pollIntervalMs },
  );
  const { data: outboundWaitingMdn, isLoading: outboundWaitingMdnLoading } = useAsyncData(
    () => listAs2OutboundMessages({ size: 1, status: 'WAITING_FOR_ASYNC_MDN' }),
    [],
    { pollIntervalMs },
  );

  const { data: inboundTotal, isLoading: inboundTotalLoading } = useAsyncData(() => listAs2InboundMessages({ size: 1 }), [], { pollIntervalMs });
  const { data: inboundFailed, isLoading: inboundFailedLoading } = useAsyncData(
    () => listAs2InboundMessages({ size: 1, status: 'FAILED' }),
    [],
    { pollIntervalMs },
  );
  const { data: inboundDeadLettered, isLoading: inboundDeadLetteredLoading } = useAsyncData(
    () => listAs2InboundMessages({ size: 1, status: 'DEAD_LETTERED' }),
    [],
    { pollIntervalMs },
  );
  const { data: inboundAwaitingMdn, isLoading: inboundAwaitingMdnLoading } = useAsyncData(
    () => listAs2InboundMessages({ size: 1, status: 'AWAITING_MDN' }),
    [],
    { pollIntervalMs },
  );

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="AS2 Gateway"
        title="AS2 dashboard"
        purpose="Operational overview of certificate health, the AS2 dead-letter queue, and outbound/inbound message posture."
        status="PRODUCTION_READY"
        connected
        next="Each tile is a live count from the AS2 gateway. Select a tile to jump to the underlying list, pre-filtered where supported."
      />

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Certificate expiry health</h2>
            <p>Grouped counts from the AS2 gateway's certificate health check.</p>
          </div>
          <div className="panel-actions">
            <PollingIndicator paused={pollingPaused} onToggle={() => setPollingPaused((paused) => !paused)} />
          </div>
        </div>
        <div className="operations-card-grid">
          {HEALTH_STATUSES.map((status) => (
            <KpiTile
              key={status}
              label={formatLabel(status)}
              value={healthLoading ? '...' : health?.certificates[status]?.length ?? 0}
              detail="Certificates currently in this health bucket."
              to={{ pathname: '/as2/certificates', search }}
            />
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Dead-letter queue</h2>
            <p>AS2 gateway delivery tasks awaiting operator action.</p>
          </div>
        </div>
        <div className="operations-card-grid">
          <KpiTile
            label="Total tasks"
            value={dlqTotalLoading ? '...' : dlqTotal?.total ?? 0}
            detail="All AS2 DLQ tasks currently tracked by the gateway."
            to={{ pathname: '/as2/dlq', search }}
          />
          <KpiTile
            label="Dead-lettered"
            value={dlqDeadLetteredLoading ? '...' : dlqDeadLettered?.total ?? 0}
            detail="Tasks that exhausted retries and need operator review."
            to={{ pathname: '/as2/dlq', search }}
          />
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Outbound messages</h2>
            <p>AS2 gateway outbound send/MDN posture.</p>
          </div>
        </div>
        <div className="operations-card-grid">
          <KpiTile
            label="Total"
            value={outboundTotalLoading ? '...' : outboundTotal?.total ?? 0}
            detail="All outbound AS2 messages tracked by the gateway."
            to={{ pathname: '/as2/outbound-messages', search }}
          />
          <KpiTile
            label="Failed"
            value={outboundFailedLoading ? '...' : outboundFailed?.total ?? 0}
            detail="Send attempts that failed."
            to={{ pathname: '/as2/outbound-messages', search: withStatus(search, 'FAILED') }}
          />
          <KpiTile
            label="Dead-lettered"
            value={outboundDeadLetteredLoading ? '...' : outboundDeadLettered?.total ?? 0}
            detail="Outbound messages that exhausted retries."
            to={{ pathname: '/as2/outbound-messages', search: withStatus(search, 'DEAD_LETTERED') }}
          />
          <KpiTile
            label="Waiting for async MDN"
            value={outboundWaitingMdnLoading ? '...' : outboundWaitingMdn?.total ?? 0}
            detail="Sent messages waiting on an asynchronous MDN from the partner."
            to={{ pathname: '/as2/outbound-messages', search: withStatus(search, 'WAITING_FOR_ASYNC_MDN') }}
          />
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Inbound messages</h2>
            <p>AS2 gateway inbound receipt/MDN posture.</p>
          </div>
        </div>
        <div className="operations-card-grid">
          <KpiTile
            label="Total"
            value={inboundTotalLoading ? '...' : inboundTotal?.total ?? 0}
            detail="All inbound AS2 messages tracked by the gateway."
            to={{ pathname: '/as2/inbound-messages', search }}
          />
          <KpiTile
            label="Failed"
            value={inboundFailedLoading ? '...' : inboundFailed?.total ?? 0}
            detail="Inbound messages that failed processing."
            to={{ pathname: '/as2/inbound-messages', search: withStatus(search, 'FAILED') }}
          />
          <KpiTile
            label="Dead-lettered"
            value={inboundDeadLetteredLoading ? '...' : inboundDeadLettered?.total ?? 0}
            detail="Inbound messages that exhausted retries."
            to={{ pathname: '/as2/inbound-messages', search: withStatus(search, 'DEAD_LETTERED') }}
          />
          <KpiTile
            label="Awaiting MDN"
            value={inboundAwaitingMdnLoading ? '...' : inboundAwaitingMdn?.total ?? 0}
            detail="Received messages still awaiting MDN generation or delivery."
            to={{ pathname: '/as2/inbound-messages', search: withStatus(search, 'AWAITING_MDN') }}
          />
        </div>
      </section>
    </div>
  );
}

function KpiTile({
  label,
  value,
  detail,
  to,
}: {
  label: string;
  value: number | string;
  detail: string;
  to: { pathname: string; search: string };
}): ReactNode {
  return (
    <Link className="operations-card" to={to}>
      <span className="operations-card-label">{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </Link>
  );
}

function withStatus(currentSearch: string, status: As2MessageStatus): string {
  const params = new URLSearchParams(currentSearch);
  params.set('status', status);
  return `?${params.toString()}`;
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
}
